// src/ChartContainer.js
import React, { useLayoutEffect, useRef, useState } from "react";
import { createChart } from "lightweight-charts";
import OHLCBox from "./OHLCBox";
import FibTool from "./FibTool";
import RangeMeasure from "./RangeMeasure";
import { initSessionBreaks } from "./sessionBreaks";
import GoToDate from "./GoToDate";
import ShapeTool from "./ShapeTool";

const API_BASE = "https://gold-5wgb.onrender.com";

console.log("API_BASE =", process.env.REACT_APP_API_URL);
console.log("NODE_ENV =", process.env.NODE_ENV);
const CHART_BACKGROUND = "#131722";
const FRESH_GET_OPTIONS = {
  method: "GET",
  cache: "no-store",
  headers: {
    "Cache-Control": "no-cache",
    Pragma: "no-cache",
  },
};

const freshApiUrl = (path, params = {}) => {
  const url = new URL(path, API_BASE);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  });
  url.searchParams.set("_", String(Date.now()));
  return url.toString();
};

const getElementSize = (element) => {
  const rect = element.getBoundingClientRect();
  return {
    width: Math.max(1, Math.round(rect.width || element.clientWidth || 1)),
    height: Math.max(1, Math.round(rect.height || element.clientHeight || 1)),
  };
};

/**
 * Optimizations:
 * - throttle crosshair DOM updates via rAF (only update when time/point changes)
 * - coalesce resize work to a single chart resize per animation frame
 * - apply canvas CSS rendering hints after data load / resize
 * - avoid repeated heavy DOM style changes
 */
export default function ChartContainer() {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const dataRef = useRef([]); // formatted candles
  const metaRef = useRef({ oldest: null, latest: null });

  const [ready, setReady] = useState(false);
  const [fibModeOn, setFibModeOn] = useState(false);

  
  // REMOVE the parseKolkataToUnix function entirely, replace with:
  const toUnix = (r) => {
    // API sends numeric Unix epoch seconds directly as "time"
    const t = Number(r.time ?? r.timestamp ?? r.date);
    return Number.isFinite(t) && t > 0 ? t : null;
  };

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let disposed = false;
    const initialSize = getElementSize(container);
    container.style.backgroundColor = CHART_BACKGROUND;

    const chart = createChart(container, {
      width: initialSize.width,
      height: initialSize.height,
      layout: {
        backgroundColor: CHART_BACKGROUND,
        textColor: "#d0d0d0",
        fontFamily: "monospace",
      },
      grid: {
        vertLines: { color: "rgba(255, 255, 255, 0.05)" },
        horzLines: { color: "rgba(255, 255, 255, 0.05)" },
      },
      crosshair: {
        mode: 0,
        vertLine: { labelVisible: false },
      },
      timeScale: {
        borderColor: "rgba(255, 255, 255, 0.1)",
        timeVisible: true,
        secondsVisible: false,
      },
      rightPriceScale: {
        borderColor: "rgba(255, 255, 255, 0.1)",
      },
    });

    chartRef.current = chart;

    // time label element (created once)
    const timeLabel = document.createElement("div");
    Object.assign(timeLabel.style, {
      position: "absolute",
      pointerEvents: "none",
      padding: "4px 8px",
      fontFamily: "monospace",
      fontSize: "12px",
      background: "#1e222d",
      color: "#d0d0d0",
      borderRadius: "4px",
      whiteSpace: "nowrap",
      display: "none",
      zIndex: 20,
      transform: "translateX(-50%)",
      bottom: "24px",
    });
    container.appendChild(timeLabel);

    const series = chart.addCandlestickSeries({
      priceFormat: { type: "price", precision: 0, minMove: 1 },
      upColor: "#089981",
      downColor: "#f23645",
      wickUpColor: "#089981",
      wickDownColor: "#f23645",
      borderUpColor: "#089981",
      borderDownColor: "#f23645",
      borderVisible: true,
      wickVisible: true,
    });
    seriesRef.current = series;

    const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    // --- throttled crosshair update (requestAnimationFrame) ---
    const crosshairState = {
      pending: false,
      lastKey: null,
      lastParam: null,
      rafId: null,
      lastUnix: null,
      lastText: "",
      lastLeft: "",
      visible: false,
    };

    const getCrosshairUnix = (param) => {
      const unix = typeof param.time === "number" ? param.time : param.time?.timestamp;
      return Number.isFinite(unix) ? unix : null;
    };

    const formatCrosshairText = (unix) => {
      if (!Number.isFinite(unix)) return null;
      const d = new Date(unix * 1000);
      const weekday = WEEKDAYS[d.getUTCDay()];
      const dd = String(d.getUTCDate()).padStart(2, "0");
      const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
      const yyyy = d.getUTCFullYear();
      const hh = String(d.getUTCHours()).padStart(2, "0");
      const min = String(d.getUTCMinutes()).padStart(2, "0");
      return `${weekday} ${dd}/${mm}/${yyyy} ${hh}:${min}`;
    };

    const applyCrosshair = (param) => {
      if (!param || !param.time || !param.point) {
        if (crosshairState.visible) {
          timeLabel.style.display = "none";
          crosshairState.visible = false;
        }
        crosshairState.lastKey = null;
        return;
      }

      const unix = getCrosshairUnix(param);
      if (unix == null) {
        if (crosshairState.visible) {
          timeLabel.style.display = "none";
          crosshairState.visible = false;
        }
        crosshairState.lastKey = null;
        return;
      }

      const roundedX = Math.round(param.point.x);
      const key = `${unix}:${roundedX}`;
      if (key === crosshairState.lastKey) return;
      crosshairState.lastKey = key;

      if (unix !== crosshairState.lastUnix) {
        const txt = formatCrosshairText(unix);
        if (txt && txt !== crosshairState.lastText) {
          timeLabel.textContent = txt;
          crosshairState.lastText = txt;
        }
        crosshairState.lastUnix = unix;
      }

      const left = `${roundedX}px`;
      if (left !== crosshairState.lastLeft) {
        timeLabel.style.left = left;
        crosshairState.lastLeft = left;
      }
      if (!crosshairState.visible) {
        timeLabel.style.display = "block";
        crosshairState.visible = true;
      }
    };

    const scheduleCrosshair = (param) => {
      crosshairState.lastParam = param;
      if (crosshairState.pending) return;
      crosshairState.pending = true;
      crosshairState.rafId = requestAnimationFrame(() => {
        crosshairState.pending = false;
        applyCrosshair(crosshairState.lastParam);
      });
    };

    const handleCrosshairMove = (p) => {
      scheduleCrosshair(p);
    };

    chart.subscribeCrosshairMove(handleCrosshairMove);

    // Lightweight Charts owns its canvas backing stores. Only apply CSS hints here;
    // changing canvas.width/canvas.height clears the drawn candles until the next chart redraw.
    const applyCanvasRenderingHints = () => {
      try {
        const canvases = container.querySelectorAll("canvas");
        canvases.forEach((c) => {
          c.style.imageRendering = "crisp-edges";
          c.style.transform = "translateZ(0)";
        });
      } catch {}
    };

    // ---------------- resize handling ----------------
    let resizeRaf = null;
    let lastChartSize = initialSize;

    const resizeChart = (force = false) => {
      const nextSize = getElementSize(container);
      if (
        !force &&
        nextSize.width === lastChartSize.width &&
        nextSize.height === lastChartSize.height
      ) {
        return;
      }

      lastChartSize = nextSize;
      try {
        if (typeof chart.resize === "function") {
          chart.resize(nextSize.width, nextSize.height, true);
        } else {
          chart.applyOptions({
            width: nextSize.width,
            height: nextSize.height,
          });
        }
      } catch {}
      applyCanvasRenderingHints();
    };

    const scheduleResize = () => {
      if (resizeRaf !== null) return;
      resizeRaf = requestAnimationFrame(() => {
        resizeRaf = null;
        resizeChart();
      });
    };

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(scheduleResize);
      resizeObserver.observe(container);
    }
    window.addEventListener("resize", scheduleResize, { passive: true });
    resizeChart(true);

    // fetch & set data (one-shot)
    (async () => {
      try {
        const path = window.location.pathname;
        const offset = parseInt(path.replace("/", ""), 10) || 0;
        const res = await fetch(freshApiUrl("/data/15min", { offset }), FRESH_GET_OPTIONS);
        if (!res.ok) throw new Error(`Failed to load candles: ${res.status}`);
        const raw = await res.json();
        if (disposed) return;
        const rows = Array.isArray(raw.candles) ? raw.candles : [];
        metaRef.current.oldest = raw.oldest_time;
        metaRef.current.latest = raw.latest_time;

        const formatted = rows
          .map((r) => {
            const t = toUnix(r);
            if (!Number.isFinite(t)) return null;
            return {
              time: t,
              open: Number(r.open),
              high: Number(r.high),
              low: Number(r.low),
              close: Number(r.close),
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.time - b.time);

        dataRef.current = formatted;
        if (formatted.length) series.setData(formatted);

        resizeChart(true);
        applyCanvasRenderingHints();
      } catch (err) {
        console.error("Failed to fetch/format candles:", err);
      } finally {
        if (!disposed) setReady(true);
      }
    })();

    // fibMode UI events
    let fibModeValue = false;
    const fibModeHandler = (ev) => {
      try {
        const next = Boolean(ev?.detail);
        if (fibModeValue !== next) {
          fibModeValue = next;
          setFibModeOn(next);
          chart.applyOptions({ crosshair: { mode: 0 } });
        }
      } catch {}
    };
    window.addEventListener("fibModeUI", fibModeHandler);

    // session breaks overlay
    const sessionBreaks = initSessionBreaks({
      container: containerRef.current,
      chart,
      series,
      dataRef,
    });

    const handleKeyDown = (e) => {
      if (e.altKey && (e.key === "r" || e.key === "R")) {
        try {
          chart.timeScale().scrollToRealTime();
          chart.priceScale("right").applyOptions({ autoScale: true });
        } catch {}
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    // cleanup
    return () => {
      disposed = true;
      window.removeEventListener("resize", scheduleResize);
      window.removeEventListener("fibModeUI", fibModeHandler);
      window.removeEventListener("keydown", handleKeyDown);
      resizeObserver?.disconnect();
      if (resizeRaf !== null) cancelAnimationFrame(resizeRaf);
      try {
        if (crosshairState.rafId) cancelAnimationFrame(crosshairState.rafId);
      } catch {}
      try {
        chart.unsubscribeCrosshairMove(handleCrosshairMove);
      } catch {}
      try {
        timeLabel.remove();
      } catch {}
      try {
        sessionBreaks?.destroy();
        chart.remove();
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div ref={containerRef} className="chart-container" />
      {ready && chartRef.current && seriesRef.current && (
        <>
          <OHLCBox
            chart={chartRef.current}
            series={seriesRef.current}
            dataRef={dataRef}
          />
          <FibTool
            chart={chartRef.current}
            series={seriesRef.current}
            containerRef={containerRef}
            dataRef={dataRef}
            oldestTime={metaRef.current.oldest}
            latestTime={metaRef.current.latest}
          />
          <RangeMeasure
            chart={chartRef.current}
            series={seriesRef.current}
            containerRef={containerRef}
            dataRef={dataRef}
          />
          <ShapeTool
            chart={chartRef.current}
            series={seriesRef.current}
            containerRef={containerRef}
            oldestTime={metaRef.current.oldest}
            latestTime={metaRef.current.latest}
          />
          <div className="controls" aria-hidden>
            <div className={fibModeOn ? "fib-indicator on" : "fib-indicator off"}>
              {fibModeOn ? "Fib Mode (Alt+F ON)" : "Press Alt+F for Fib"}
            </div>
          </div>
          <GoToDate
            chart={chartRef.current}
            dataRef={dataRef}
            containerRef={containerRef}
          />
        </>
      )}
    </>
  );
}
